import React, { useEffect, useMemo, useRef, useState } from 'react';
import Button from './Button';

type BatchUploadItem = {
  id: string;
  file: File;
  studentName: string;
  autoFilled: boolean;
};

interface BatchUploadPanelProps {
  assessmentName?: string;
  onStartBatch?: (items: Array<{ file: File; studentName: string }>) => void;
  isProcessing?: boolean;
}

const getBaseName = (fileName: string) => fileName.replace(/\.[^/.]+$/, '');

const BatchUploadPanel: React.FC<BatchUploadPanelProps> = ({
  assessmentName,
  onStartBatch,
  isProcessing = false,
}) => {
  const [uploads, setUploads] = useState<BatchUploadItem[]>([]);
  const [useFilenames, setUseFilenames] = useState(true);
  const [status, setStatus] = useState<string | null>(null);
  const pdfInputRef = useRef<HTMLInputElement | null>(null);
  const docInputRef = useRef<HTMLInputElement | null>(null);

  const allowStart = uploads.length > 0 && uploads.every((item) => item.studentName.trim().length > 0);

  const acceptedDocs = useMemo(
    () => [
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.apple.pages',
      'application/pdf',
      'application/rtf',
    ],
    []
  );

  const addFiles = (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) return;
    const incoming = Array.from(fileList);
    const next: BatchUploadItem[] = [];
    const existingSignature = new Set(uploads.map((item) => `${item.file.name}-${item.file.size}`));

    incoming.forEach((file, index) => {
      const extension = file.name.split('.').pop()?.toLowerCase();
      const extensionAllowed = extension ? ['pdf', 'doc', 'docx', 'pages', 'odt', 'rtf'].includes(extension) : false;
      const typeAllowed = file.type === 'application/pdf' || acceptedDocs.includes(file.type);
      if (!typeAllowed && !extensionAllowed) {
        setStatus(`"${file.name}" is not a supported PDF or document type.`);
        return;
      }
      const signature = `${file.name}-${file.size}`;
      if (existingSignature.has(signature)) {
        return; // skip duplicates by name + size
      }
      next.push({
        id: `${Date.now()}-${index}-${file.name}`,
        file,
        studentName: useFilenames ? getBaseName(file.name) : '',
        autoFilled: useFilenames,
      });
    });

    if (next.length === 0) {
      setStatus('No new files were added.');
      return;
    }

    setUploads((prev) => [...prev, ...next]);
    setStatus(`${next.length} file${next.length > 1 ? 's' : ''} added. ${useFilenames ? 'Names auto-filled from filenames.' : ''}`);
  };

  const handleNameChange = (id: string, name: string) => {
    setUploads((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, studentName: name, autoFilled: false } : item
      )
    );
  };

  const handleRemove = (id: string) => {
    setUploads((prev) => prev.filter((item) => item.id !== id));
  };

  useEffect(() => {
    if (!useFilenames) return;
    setUploads((prev) =>
      prev.map((item) => ({
        ...item,
        studentName: item.studentName || getBaseName(item.file.name),
        autoFilled: true,
      }))
    );
  }, [useFilenames]);

  const handleStart = () => {
    if (!allowStart) {
      setStatus('Add files and make sure each has a student name or number.');
      return;
    }
    onStartBatch?.(uploads.map(({ file, studentName }) => ({ file, studentName })));
    setStatus('Batch marking pipeline ready - connect backend processing when available.');
  };

  return (
    <div className="bg-white border border-orange-200 rounded-2xl shadow-soft">
      <div className="flex items-center justify-end gap-2 px-6 py-4 border-b border-orange-100 bg-gradient-to-r from-orange-50 to-white rounded-t-2xl">
        <div className="flex items-center gap-2 text-sm text-gray-700">
          <input
            id="useFilenames"
            type="checkbox"
            className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
            checked={useFilenames}
            onChange={(e) => setUseFilenames(e.target.checked)}
          />
          <label htmlFor="useFilenames" className="select-none">
            Auto-fill names from file names
          </label>
        </div>
      </div>

      <div className="p-6 space-y-4">
        <div className="flex flex-wrap gap-3">
          <Button
            onClick={() => pdfInputRef.current?.click()}
            variant="secondary"
            className="border-orange-200 text-orange-700 bg-orange-50 hover:bg-orange-100"
            disabled={isProcessing}
          >
            Upload PDFs
          </Button>
          <Button
            onClick={() => docInputRef.current?.click()}
            variant="secondary"
            className="border-orange-200 text-orange-700 bg-orange-50 hover:bg-orange-100"
            disabled={isProcessing}
          >
            Upload documents
          </Button>
          <input
            ref={pdfInputRef}
            type="file"
            multiple
            accept=".pdf,application/pdf"
            className="hidden"
            onChange={(e) => {
              addFiles(e.target.files);
              if (e.target) e.target.value = '';
            }}
          />
          <input
            ref={docInputRef}
            type="file"
            multiple
            accept=".doc,.docx,.pages,.odt,.rtf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.apple.pages,application/rtf"
            className="hidden"
            onChange={(e) => {
              addFiles(e.target.files);
              if (e.target) e.target.value = '';
            }}
          />
        </div>

        {uploads.length === 0 ? (
          <div className="border border-dashed border-orange-200 rounded-xl p-5 text-center text-sm text-gray-600 bg-orange-50/60">
            No batch files yet. Add PDFs or documents - each file will get its own student name/number field.
          </div>
        ) : (
          <div className="space-y-3">
            {uploads.map((item, index) => (
              <div
                key={item.id}
                className="flex flex-col sm:flex-row sm:items-center gap-3 p-4 border border-gray-200 rounded-xl bg-white shadow-sm"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 flex items-center justify-center rounded-lg bg-orange-50 text-orange-700 font-semibold">
                      {index + 1}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-midnight truncate" title={item.file.name}>
                        {item.file.name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {(item.file.size / 1024 / 1024).toFixed(2)} MB | {item.file.type || 'Unknown type'}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="flex-1 w-full">
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    Student name or number
                  </label>
                  <input
                    type="text"
                    value={item.studentName}
                    onChange={(e) => handleNameChange(item.id, e.target.value)}
                    placeholder="e.g. Mia Chen or Student #12"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  />
                  {item.autoFilled && (
                    <p className="text-[11px] text-orange-600 mt-1">Auto-filled from filename - edit if needed.</p>
                  )}
                </div>
                <div className="flex-shrink-0">
                  <Button variant="danger" size="sm" onClick={() => handleRemove(item.id)}>
                    Remove
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-t border-gray-100 pt-4">
          <div className="text-sm text-gray-600">
            {uploads.length === 0
              ? 'Add files to enable batch marking.'
              : `Ready to mark ${uploads.length} file${uploads.length > 1 ? 's' : ''}.`}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={() => {
                setUploads([]);
                setStatus('Batch list cleared.');
              }}
              variant="secondary"
              disabled={uploads.length === 0}
            >
              Clear list
            </Button>
            <Button
              onClick={handleStart}
              className="bg-orange-600 hover:bg-orange-700"
              disabled={!allowStart || isProcessing}
            >
              {isProcessing ? 'Preparing batch...' : 'Start batch marking'}
            </Button>
          </div>
        </div>

        {status && (
          <div className="rounded-lg bg-orange-50 border border-orange-200 px-3 py-2 text-sm text-orange-800">
            {status}
          </div>
        )}
      </div>
    </div>
  );
};

export default BatchUploadPanel;
